import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Shield, CreditCard, HeadphonesIcon, Sparkles, Star } from "lucide-react";
import api, { resolveImageUrl } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SearchWidget from "@/components/SearchWidget";
import HotelCard from "@/components/HotelCard";
import AdSlot from "@/components/AdSlot";
import { useSiteContent } from "@/lib/siteContent";

const HERO_IMG_DEFAULT = "https://images.unsplash.com/photo-1582498674105-ad104fcc5784?w=1920&q=80";

// Default order (fallback when admin hasn't configured home_sections yet)
const DEFAULT_SECTIONS = [
    { id: "s_hero", type: "hero", enabled: true, order: 1 },
    { id: "s_trust", type: "trust_bar", enabled: true, order: 2 },
    { id: "s_dest", type: "destinations", enabled: true, order: 3 },
    { id: "s_feat", type: "featured_hotels", enabled: true, order: 4 },
    { id: "s_deals", type: "deals", enabled: true, order: 5 },
    { id: "s_reviews", type: "reviews", enabled: true, order: 6 },
];

export default function HomePage() {
    const site = useSiteContent();
    const home = site?.home || {};
    const brand = site?.brand || {};
    const heroImg = home.hero_image_url ? resolveImageUrl(home.hero_image_url) : HERO_IMG_DEFAULT;
    const [destinations, setDestinations] = useState([]);
    const [featured, setFeatured] = useState([]);
    const [deals, setDeals] = useState([]);
    const [loading, setLoading] = useState(true);

    const sections = useMemo(() => {
        const raw = (site?.home_sections && site.home_sections.length > 0) ? site.home_sections : DEFAULT_SECTIONS;
        return raw.slice().sort((a, b) => (a.order || 0) - (b.order || 0)).filter((s) => s.enabled !== false);
    }, [site?.home_sections]);

    useEffect(() => {
        Promise.all([
            api.get("/destinations?home_only=true").then((r) => {
                if (r.data?.length) return setDestinations(r.data);
                return api.get("/destinations").then((r2) => setDestinations(r2.data));
            }),
            api.get("/hotels/featured?limit=8").then((r) => setFeatured(r.data)),
            api.get("/hotels/deals?limit=6").then((r) => setDeals(r.data)),
        ]).finally(() => setLoading(false));
    }, []);

    const renderSection = (s) => {
        switch (s.type) {
            case "hero":
                return <HeroSection key={s.id} brand={brand} home={home} heroImg={heroImg} />;
            case "trust_bar":
                return <TrustBarSection key={s.id} />;
            case "destinations":
                return <DestinationsSection key={s.id} home={home} destinations={destinations} />;
            case "featured_hotels":
                return <FeaturedSection key={s.id} home={home} featured={featured} loading={loading} />;
            case "deals":
                return <DealsSection key={s.id} home={home} deals={deals} />;
            case "reviews":
                return <ReviewsSection key={s.id} />;
            default:
                return null;
        }
    };

    return (
        <div data-testid="home-page" className="bg-slate-50 dark:bg-slate-950 min-h-screen">
            <Navbar />
            {sections.map(renderSection)}
            <Footer />
        </div>
    );
}

// ---------- Section components ----------
function HeroSection({ brand, home, heroImg }) {
    return (
        <section className="relative" data-testid="section-hero">
            <div className="relative h-[520px] md:h-[600px] overflow-hidden">
                <img src={heroImg} alt="" className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 hero-overlay" />
                <div className="relative h-full container mx-auto px-4 md:px-6 max-w-7xl flex flex-col justify-center">
                    <span className="text-amber-300 font-semibold tracking-widest text-sm uppercase mb-3 animate-fade-up" style={{ animationDelay: "0.05s" }}>{brand.tagline || "msonlinegroup.com"}</span>
                    <h1 data-testid="hero-title" className="font-display text-4xl md:text-6xl lg:text-7xl font-bold text-white text-balance leading-[1.05] max-w-3xl animate-fade-up" style={{ animationDelay: "0.15s" }}>
                        {home.hero_title || "Hayalindeki Tatili Keşfet"}
                    </h1>
                    <p data-testid="hero-subtitle" className="text-lg md:text-xl text-slate-200 mt-4 max-w-2xl animate-fade-up" style={{ animationDelay: "0.25s" }}>
                        {home.hero_subtitle || "Türkiye'nin en seçkin 150+ otelinde, en iyi fiyat garantisiyle online rezervasyon."}
                    </p>
                </div>
            </div>
            <div id="search-widget-anchor" className="container mx-auto px-4 md:px-6 max-w-6xl -mt-24 md:-mt-20 relative z-20 pb-2">
                <SearchWidget />
            </div>
            <div className="container mx-auto px-4 md:px-6 max-w-7xl mt-6">
                <AdSlot slot="homepage_hero_below" className="aspect-[16/3] md:aspect-[21/4]" />
            </div>
        </section>
    );
}

function TrustBarSection() {
    return (
        <section className="container mx-auto px-4 md:px-6 max-w-7xl mt-16" data-testid="section-trust">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                    { icon: <Shield className="h-6 w-6" />, title: "Güvenli Ödeme", desc: "Stripe 3D Secure" },
                    { icon: <CreditCard className="h-6 w-6" />, title: "En İyi Fiyat", desc: "Garanti" },
                    { icon: <Sparkles className="h-6 w-6" />, title: "Anında Onay", desc: "E-posta + SMS" },
                    { icon: <HeadphonesIcon className="h-6 w-6" />, title: "7/24 Destek", desc: "Türkçe ekip" },
                ].map((it, i) => (
                    <div key={i} className="flex items-center gap-3 p-4 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800">
                        <div className="h-12 w-12 rounded-xl bg-sky-50 dark:bg-sky-950 text-sky-600 flex items-center justify-center">{it.icon}</div>
                        <div>
                            <div className="font-semibold text-slate-900 dark:text-white">{it.title}</div>
                            <div className="text-sm text-slate-500">{it.desc}</div>
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );
}

function DestinationsSection({ home, destinations }) {
    return (
        <section id="destinations" className="container mx-auto px-4 md:px-6 max-w-7xl py-20" data-testid="section-destinations">
            <div className="flex items-end justify-between mb-8">
                <div>
                    <span className="text-xs font-bold uppercase tracking-widest text-sky-500">{home.destinations_title || "Popüler Destinasyonlar"}</span>
                    <h2 className="font-display text-3xl md:text-4xl font-bold mt-2 text-slate-900 dark:text-white">{"Türkiye'nin gözde tatil noktaları"}</h2>
                </div>
                <Link to="/search" className="hidden md:inline-flex items-center gap-1 text-sm font-semibold text-sky-500 hover:text-sky-600">
                    Tümünü gör <ArrowRight className="h-4 w-4" />
                </Link>
            </div>
            {destinations.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                    {destinations.slice(0, 6).map((d, idx) => {
                        const layouts = [
                            "md:col-span-6 md:row-span-2 aspect-[4/5] md:aspect-auto md:h-[480px]",
                            "md:col-span-3 aspect-[3/2] md:h-[232px]",
                            "md:col-span-3 aspect-[3/2] md:h-[232px]",
                            "md:col-span-4 aspect-[4/3] md:h-[232px]",
                            "md:col-span-4 aspect-[4/3] md:h-[232px]",
                            "md:col-span-4 aspect-[4/3] md:h-[232px]",
                        ];
                        return (
                            <Link key={d.slug} to={`/search?destination=${d.slug}`} data-testid={`destination-${d.slug}`} className={`group relative overflow-hidden rounded-2xl ${layouts[idx]}`}>
                                <img src={d.image} alt={d.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/20 to-transparent" />
                                <div className="absolute bottom-0 left-0 right-0 p-5 text-white">
                                    <h3 className="font-display text-2xl md:text-3xl font-bold">{d.name}</h3>
                                    <p className="text-sm text-white/80 mt-1">{d.hotel_count || 0} otel · {d.region}</p>
                                </div>
                            </Link>
                        );
                    })}
                </div>
            )}
        </section>
    );
}

function FeaturedSection({ home, featured, loading }) {
    return (
        <section className="container mx-auto px-4 md:px-6 max-w-7xl pb-10" data-testid="section-featured">
            <div className="flex items-end justify-between mb-6">
                <div>
                    <span className="text-xs font-bold uppercase tracking-widest text-sky-500">{home.featured_title || "Öne Çıkan Oteller"}</span>
                    <h2 className="font-display text-3xl md:text-4xl font-bold mt-2 text-slate-900 dark:text-white">En çok tercih edilenler</h2>
                </div>
                <Link to="/search?featured=1" className="hidden md:inline-flex items-center gap-1 text-sm font-semibold text-sky-500 hover:text-sky-600">
                    Tümünü gör <ArrowRight className="h-4 w-4" />
                </Link>
            </div>
            {loading && <div className="text-slate-500">Yükleniyor...</div>}
            {featured.length > 0 && (
                <div className="mb-8 overflow-x-auto -mx-2 px-2 pb-2 scrollbar-thin" data-testid="featured-stories">
                    <div className="flex items-start gap-5 md:gap-7 min-w-max">
                        {featured.map((h) => (
                            <Link key={`story-${h.id}`} to={`/hotel/${h.id}`} data-testid={`story-${h.id}`} className="group flex flex-col items-center flex-shrink-0 w-20 md:w-24">
                                <div className="relative">
                                    <div className="absolute inset-0 rounded-full bg-gradient-to-tr from-amber-400 via-pink-500 to-fuchsia-600 p-[3px] group-hover:scale-110 transition-transform duration-300">
                                        <div className="w-full h-full rounded-full bg-white dark:bg-slate-950" />
                                    </div>
                                    <img src={resolveImageUrl(h.main_image)} alt={h.name} className="relative w-20 h-20 md:w-24 md:h-24 rounded-full object-cover ring-2 ring-white dark:ring-slate-950 m-[3px]" />
                                    {h.rating != null && (
                                        <div className="absolute -bottom-1 -right-1 bg-sky-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full shadow-md flex items-center gap-0.5">
                                            <Star className="h-2.5 w-2.5 fill-white" />
                                            {h.rating}
                                        </div>
                                    )}
                                </div>
                                <div className="mt-2 text-center w-full">
                                    <div className="text-xs font-semibold text-slate-900 dark:text-white truncate">{h.name}</div>
                                    <div className="text-[10px] text-slate-500 truncate">{h.destination_name}</div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {featured.map((h) => <HotelCard key={h.id} hotel={h} />)}
            </div>
        </section>
    );
}

function DealsSection({ home, deals }) {
    return (
        <section id="deals" className="bg-gradient-to-br from-sky-500 to-sky-700 py-16 mt-10" data-testid="section-deals">
            <div className="container mx-auto px-4 md:px-6 max-w-7xl">
                <div className="flex items-end justify-between mb-8 text-white">
                    <div>
                        <span className="text-xs font-bold uppercase tracking-widest text-amber-300">{home.deals_title || "Son Dakika Kampanyaları"}</span>
                        <h2 className="font-display text-3xl md:text-4xl font-bold mt-2">{"Kaçırma! %40'a varan indirimler"}</h2>
                    </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {deals.map((h) => <HotelCard key={h.id} hotel={h} />)}
                </div>
            </div>
        </section>
    );
}

function ReviewsSection() {
    return (
        <section className="container mx-auto px-4 md:px-6 max-w-7xl py-20" data-testid="section-reviews">
            <div className="text-center mb-12">
                <span className="text-xs font-bold uppercase tracking-widest text-sky-500">Müşteri Yorumları</span>
                <h2 className="font-display text-3xl md:text-4xl font-bold mt-2 text-slate-900 dark:text-white">300.000+ mutlu misafir</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[
                    { name: "Ayşe K.", city: "İstanbul", text: "Antalya rezervasyonum 5 dakikada tamamlandı. Otel beklediğimden çok daha güzeldi!", rating: 5 },
                    { name: "Burak T.", city: "Ankara", text: "İndirimli fiyatlar gerçek. Booking ile karşılaştırdım, Msonline Travel her seferinde daha uygun.", rating: 5 },
                    { name: "Selin D.", city: "İzmir", text: "Müşteri hizmetleri Türkçe ve hızlı. Sorunsuz bir tatil deneyimi yaşadık.", rating: 5 },
                ].map((r, i) => (
                    <div key={i} className="p-6 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800">
                        <div className="flex gap-0.5 mb-3">
                            {[...Array(r.rating)].map((_, idx) => <Star key={idx} className="h-4 w-4 fill-amber-400 text-amber-400" />)}
                        </div>
                        <p className="text-slate-700 dark:text-slate-200 leading-relaxed mb-4">{`"${r.text}"`}</p>
                        <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-full bg-gradient-to-br from-sky-500 to-amber-400 flex items-center justify-center text-white font-bold">
                                {r.name[0]}
                            </div>
                            <div>
                                <div className="font-semibold text-slate-900 dark:text-white">{r.name}</div>
                                <div className="text-xs text-slate-500">{r.city}</div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );
}
